import React, { useEffect, useState, useCallback } from "react";
import ThemedBackground from "@/components/ThemedBackground";
import { api } from "@/lib/api";

/**
 * ChatHistory page at /history — Lists every conversation session with
 * the assistant. Click a session → loads full transcript + LLM-generated
 * summary (cached server-side). Built for the owner's request: "Chat
 * History Summary page hitting /api/jarvis/sessions with an LLM summary
 * per session."
 */

const SENTIMENT_COLORS = {
  happy: "#10b981", excited: "#22d3ee",
  neutral: "#94a3b8", tired: "#f59e0b", frustrated: "#ef4444",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ChatHistory() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summLoading, setSummLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/jarvis/sessions");
      setSessions(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSession = useCallback(async (sid) => {
    setActiveId(sid);
    setTurns([]);
    setSummary(null);
    try {
      const r = await api.get(`/jarvis/sessions/${sid}`);
      setTurns(r.data?.turns || []);
    } catch (_) { /* ignore */ }
    setSummLoading(true);
    try {
      const r = await api.get(`/jarvis/sessions/${sid}/summary`);
      setSummary(r.data || null);
    } catch (_) {
      setSummary({ summary: "(Summary unavailable)", topics: [], unresolved: [] });
    } finally {
      setSummLoading(false);
    }
  }, []);

  const regenSummary = async () => {
    if (!activeId) return;
    setSummLoading(true);
    try {
      await api.delete(`/jarvis/sessions/${activeId}/summary`);
      const r = await api.get(`/jarvis/sessions/${activeId}/summary`);
      setSummary(r.data || null);
    } finally { setSummLoading(false); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this whole conversation?")) return;
    await api.delete(`/jarvis/sessions/${sid}`);
    if (activeId === sid) { setActiveId(null); setTurns([]); setSummary(null); }
    await load();
  };

  return (
    <div className="min-h-screen text-[var(--rc-text)] relative" data-testid="history-page">
      <ThemedBackground background="neural_brain" lite={true} />
      <div className="relative z-10 p-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => { window.location.href = "/"; }}
            className="rc-panel px-3 py-1.5 text-[10px] rc-glow-text"
            data-testid="history-back-btn"
          >← BACK</button>
          <h1 className="text-2xl font-bold rc-glow-text">CHAT HISTORY</h1>
          <span className="text-[11px] opacity-60">{sessions.length} sessions</span>
          <button onClick={load} className="ml-auto rc-panel px-3 py-1.5 text-[10px] rc-glow-text"
                  data-testid="history-refresh-btn">↻ REFRESH</button>
        </div>

        {err && <div className="rc-panel p-3 text-[11px] mb-3" style={{ color: "#f87171" }}>{err}</div>}

        <div className="grid gap-4" style={{ gridTemplateColumns: "320px 1fr" }}>
          {/* Sessions list */}
          <div className="rc-panel p-2 overflow-y-auto" style={{ maxHeight: "80vh" }} data-testid="history-sessions">
            {loading && <div className="text-[11px] opacity-60 p-2">Loading…</div>}
            {!loading && sessions.length === 0 && <div className="text-[11px] opacity-60 p-2">No conversations yet.</div>}
            {sessions.map((s) => (
              <div key={s.session_id}
                   onClick={() => openSession(s.session_id)}
                   className="p-2 cursor-pointer rounded mb-1 hover:opacity-100"
                   style={{
                     background: activeId === s.session_id ? "rgba(255,215,0,0.12)" : "transparent",
                     opacity: activeId === s.session_id ? 1 : 0.85,
                     borderLeft: activeId === s.session_id ? "2px solid var(--rc-gold)" : "2px solid transparent",
                   }}
                   data-testid={`history-session-${s.session_id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] rc-glow-text font-semibold">
                    {fmtDate(s.last_active || s.started_at)}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                          className="text-[10px] opacity-50 hover:opacity-100 hover:text-[#f87171]"
                          data-testid={`history-delete-${s.session_id}`}>×</button>
                </div>
                <div className="text-[10px] opacity-70 mt-0.5">{s.turn_count} turns</div>
                {s.first_message && (
                  <div className="text-[10px] opacity-60 mt-1 line-clamp-2">{s.first_message.slice(0, 80)}</div>
                )}
              </div>
            ))}
          </div>

          {/* Detail pane */}
          <div className="space-y-3">
            {!activeId && (
              <div className="rc-panel p-6 text-center text-[12px] opacity-60">
                Pick a conversation from the left to view its transcript and AI summary.
              </div>
            )}

            {activeId && (
              <>
                {/* Summary card */}
                <div className="rc-panel p-4" data-testid="history-summary">
                  <div className="flex items-center justify-between mb-2">
                    <div className="rc-label text-[10px] rc-glow-text">// AI SUMMARY</div>
                    <button onClick={regenSummary} disabled={summLoading}
                            className="text-[10px] rc-glow-text hover:opacity-80 disabled:opacity-50"
                            data-testid="history-regen-btn">
                      {summLoading ? "…regenerating" : "↻ Regenerate"}
                    </button>
                  </div>
                  {summLoading && !summary && <div className="text-[11px] opacity-60">Asking Jarvis to summarise…</div>}
                  {summary && (
                    <>
                      <div className="text-[12px] mb-2">{summary.summary}</div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(summary.topics || []).map((t) => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 rounded"
                                style={{ background: "rgba(255,215,0,0.15)", color: "var(--rc-gold)" }}>
                            {t}
                          </span>
                        ))}
                        {summary.sentiment && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                                style={{ background: SENTIMENT_COLORS[summary.sentiment] || "#94a3b8", color: "#0a1628" }}>
                            {summary.sentiment.toUpperCase()}
                          </span>
                        )}
                      </div>
                      {summary.unresolved && summary.unresolved.length > 0 && (
                        <div>
                          <div className="text-[10px] mb-1 opacity-80">⚠ Unresolved / open items:</div>
                          <ul className="text-[11px] list-disc pl-5 space-y-0.5">
                            {summary.unresolved.map((u, i) => <li key={i}>{u}</li>)}
                          </ul>
                        </div>
                      )}
                      {summary.cached && (
                        <div className="text-[8px] opacity-50 mt-2">↳ cached summary · click ↻ Regenerate for a fresh take</div>
                      )}
                    </>
                  )}
                </div>

                {/* Transcript */}
                <div className="rc-panel p-4" data-testid="history-transcript">
                  <div className="rc-label text-[10px] rc-glow-text mb-2">// TRANSCRIPT ({turns.length} turns)</div>
                  <div className="space-y-2 overflow-y-auto" style={{ maxHeight: "55vh" }}>
                    {turns.map((t, i) => (
                      <div key={i} className="text-[11px]"
                           style={{ borderLeft: `2px solid ${t.role === "user" ? "var(--rc-gold)" : "rgba(34,211,238,0.6)"}`, paddingLeft: 8 }}>
                        <div className="text-[9px] opacity-60">
                          {t.role === "user" ? "OWNER" : "JARVIS"} · {fmtDate(t.created_at)}
                        </div>
                        <div className="text-[11px] whitespace-pre-wrap">{t.text}</div>
                      </div>
                    ))}
                    {turns.length === 0 && <div className="text-[11px] opacity-60">No turns recorded.</div>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
